import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import debounce from 'lodash.debounce';

import { GroupedOption, SelectOption } from '~/shared/utils/types';

interface UseSearchFilterOptions {
  initialSelections: SelectOption[];
  onSelectionsChange: (selections: SelectOption[]) => void;
  searchFunction: (pattern: string) => Promise<GroupedOption[]>;
  filterGroup: string;
  filterGroupMatcher?: (group: string) => boolean;
  debounceMs?: number;
  defaultQuery?: string;
  isDisclosureOpen?: boolean;
  /** When provided, selection matching uses this key instead of raw option id. */
  optionSelectionKey?: (option: SelectOption) => string;
}

function defaultSelectionKey(option: SelectOption): string {
  return option.id;
}

function mergeSelectedIntoSearchResults({
  results,
  selections,
  matchesGroup,
  getSelectionKey,
}: {
  results: GroupedOption[];
  selections: SelectOption[];
  matchesGroup: (group: string) => boolean;
  getSelectionKey: (option: SelectOption) => string;
}): GroupedOption[] {
  const currentFilteredSelections = selections.filter((s) => matchesGroup(s.group));
  const resultsWithoutPrepended = results[0]?.label === '' ? results.slice(1) : results;

  if (currentFilteredSelections.length === 0) {
    return resultsWithoutPrepended;
  }

  const resultKeys = new Set(
    resultsWithoutPrepended.flatMap((group) => group.options.map(getSelectionKey))
  );

  const selectedNotInResults = currentFilteredSelections.filter(
    (selected) => !resultKeys.has(getSelectionKey(selected))
  );

  if (selectedNotInResults.length === 0) {
    return resultsWithoutPrepended;
  }

  return [{ label: '', options: selectedNotInResults }, ...resultsWithoutPrepended];
}

export function useSearchFilter({
  initialSelections,
  onSelectionsChange,
  searchFunction,
  filterGroup,
  filterGroupMatcher,
  debounceMs = 300,
  defaultQuery,
  isDisclosureOpen,
  optionSelectionKey,
}: UseSearchFilterOptions) {
  const getSelectionKey = optionSelectionKey ?? defaultSelectionKey;

  const matchesGroup = useMemo(
    () =>
      filterGroupMatcher ||
      ((group: string) =>
        group === filterGroup || (filterGroup === 'tags' && group.startsWith('tag:'))),
    [filterGroupMatcher, filterGroup]
  );

  const [searchResults, setSearchResults] = useState<GroupedOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isSelecting, setIsSelecting] = useState(false);
  const [localSelections, setLocalSelections] = useState<SelectOption[]>(initialSelections);

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const mouseDownOnResults = useRef(false);
  const searchFunctionRef = useRef(searchFunction);
  const localSelectionsRef = useRef(localSelections);
  const matchesGroupRef = useRef(matchesGroup);
  const getSelectionKeyRef = useRef(getSelectionKey);

  useEffect(() => {
    searchFunctionRef.current = searchFunction;
  }, [searchFunction]);

  useEffect(() => {
    localSelectionsRef.current = localSelections;
  }, [localSelections]);

  useEffect(() => {
    matchesGroupRef.current = matchesGroup;
  }, [matchesGroup]);

  useEffect(() => {
    getSelectionKeyRef.current = getSelectionKey;
  }, [getSelectionKey]);

  useEffect(() => {
    setLocalSelections(initialSelections);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelections]);

  useEffect(() => {
    if (focusedIndex >= 0 && resultsRef.current) {
      const focusedElement = resultsRef.current.querySelector(
        `[data-option-index="${focusedIndex}"]`
      );
      if (focusedElement) {
        focusedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [focusedIndex]);

  const searchIdRef = useRef(0);

  const debouncedSearch = useMemo(
    () =>
      debounce((pattern: string, searchId: number) => {
        searchFunctionRef.current(pattern)
          .then((results) => {
            if (searchIdRef.current !== searchId) {
              return;
            }

            if (!results) {
              setIsLoading(false);
              return;
            }

            const merged = mergeSelectedIntoSearchResults({
              results,
              selections: localSelectionsRef.current,
              matchesGroup: matchesGroupRef.current,
              getSelectionKey: getSelectionKeyRef.current,
            });

            setSearchResults(merged);
            setFocusedIndex(-1);
            setIsLoading(false);
          })
          .catch(() => {
            setSearchResults([]);
            setIsLoading(false);
          });
      }, debounceMs),
    [debounceMs]
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const flattenedResults = useMemo(
    () => searchResults.flatMap((group) => group.options),
    [searchResults]
  );

  const selectedIdSet = useMemo(() => new Set(localSelections.map((s) => s.id)), [localSelections]);

  const isOptionSelected = useCallback(
    (option: SelectOption) =>
      localSelections.some(
        (selection) =>
          matchesGroup(selection.group) &&
          getSelectionKey(selection) === getSelectionKey(option)
      ),
    [localSelections, matchesGroup, getSelectionKey]
  );

  // Empty input: seed the list when the query clears (or on first mount).
  // Intentionally NOT keyed on localSelections — toggling a checkbox while the input
  // is empty should update tick marks via isOptionSelected, not rebuild the list.
  useEffect(() => {
    if (inputValue.trim()) {
      return;
    }

    const filteredSelections = localSelectionsRef.current.filter((selection) =>
      matchesGroupRef.current(selection.group)
    );

    if (filteredSelections.length > 0) {
      setSearchResults([{ label: '', options: filteredSelections }]);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
    setFocusedIndex(-1);
    setIsLoading(false);
  }, [inputValue, matchesGroup]);

  // Active search: run when the query changes.
  useEffect(() => {
    if (!inputValue.trim()) {
      return;
    }

    setIsLoading(true);
    setShowResults(true);

    searchIdRef.current += 1;
    const currentSearchId = searchIdRef.current;

    debouncedSearch(inputValue, currentSearchId);

    return () => {
      debouncedSearch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, debouncedSearch, matchesGroup]);

  // While a search query is active, re-merge selections without replacing the result list.
  useEffect(() => {
    if (!inputValue.trim()) {
      return;
    }

    setSearchResults((currentResults) => {
      const hasSearchResults = currentResults.some((group) => group.label !== '');
      if (!hasSearchResults) {
        return currentResults;
      }

      return mergeSelectedIntoSearchResults({
        results: currentResults,
        selections: localSelections,
        matchesGroup,
        getSelectionKey,
      });
    });
  }, [localSelections, inputValue, matchesGroup, getSelectionKey]);

  useEffect(() => {
    if (
      isDisclosureOpen &&
      defaultQuery !== undefined &&
      !inputValue.trim() &&
      searchResults.length === 0
    ) {
      setIsLoading(true);
      setShowResults(true);
      searchFunctionRef.current(defaultQuery)
        .then((results) => {
          if (!results || results.length === 0) {
            setSearchResults([]);
          } else {
            const merged = mergeSelectedIntoSearchResults({
              results,
              selections: localSelectionsRef.current,
              matchesGroup: matchesGroupRef.current,
              getSelectionKey: getSelectionKeyRef.current,
            });
            setSearchResults(merged);
          }
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [
    isDisclosureOpen,
    defaultQuery,
    inputValue,
    searchResults.length,
    searchFunction,
  ]);

  useEffect(() => {
    if (isDisclosureOpen && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 150);

      return () => clearTimeout(timeoutId);
    }
  }, [isDisclosureOpen]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setShowResults(true);
    setFocusedIndex(-1);
  };

  const handleInputFocus = () => {
    if (!inputValue.trim()) {
      setShowResults(true);
    }
  };

  const handleInputBlur = () => {
    setTimeout(() => {
      if (
        !isSelecting &&
        !resultsRef.current?.contains(document.activeElement) &&
        !mouseDownOnResults.current
      ) {
        setFocusedIndex(-1);
      }
    }, 150);
  };

  const handleSelect = (option: SelectOption) => {
    setIsSelecting(true);
    setShowResults(true);

    const optionKey = getSelectionKey(option);
    const isSelected = localSelections.some(
      (selection) =>
        matchesGroup(selection.group) && getSelectionKey(selection) === optionKey
    );

    const newSelections: SelectOption[] = isSelected
      ? localSelections.filter(
          (selection) =>
            !(matchesGroup(selection.group) && getSelectionKey(selection) === optionKey)
        )
      : [...localSelections, option];

    setLocalSelections(newSelections);
    onSelectionsChange(newSelections);

    inputRef.current?.focus();

    setTimeout(() => {
      setIsSelecting(false);
    }, 300);
  };

  const handleResultsMouseDown = () => {
    mouseDownOnResults.current = true;
  };

  const handleResultsMouseUp = () => {
    setTimeout(() => {
      mouseDownOnResults.current = false;
    }, 100);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showResults || flattenedResults.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev < flattenedResults.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : flattenedResults.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && flattenedResults[focusedIndex]) {
          handleSelect(flattenedResults[focusedIndex]);
        } else if (flattenedResults.length === 1) {
          handleSelect(flattenedResults[0]);
        }
        break;
      case 'Escape':
        setShowResults(false);
        setFocusedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  return {
    searchResults,
    isLoading,
    inputValue,
    showResults,
    focusedIndex,
    localSelections,
    selectedIdSet,
    isOptionSelected,
    flattenedResults,

    inputRef,
    resultsRef,

    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleSelect,
    handleResultsMouseDown,
    handleResultsMouseUp,
    handleKeyDown,
  };
}
